// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * React hook for fetching and managing collections of assets with sorting and filtering.
 * This module provides comprehensive functionality for requesting, caching, sorting,
 * and filtering asset data from the remote API using React Query.
 */
import {
  AssetFilter,
  getAssets,
  serializeAssetFilter,
} from '@/asset/api/get-assets';
import { ONE_MINUTE_MS, TWO_MINUTES_MS } from '@/utilities/time-constants';
import { useSuspenseQuery } from '@tanstack/react-query';

/**
 * Configuration options for the useAssets hook.
 *
 * @property {AssetFilter} [filter] - Optional filtering criteria
 */
interface UseAssetsOptions {
  filter?: AssetFilter;
}

/**
 * A React hook that fetches and manages a collection of assets with sorting and filtering.
 * Uses React Query's suspense mode for loading states and automatic background
 * revalidation. The hook will suspend the component while the initial data is
 * being fetched.
 *
 * The hook supports:
 * - Filtering assets based on various criteria (ownership, location, status, etc.)
 * - Sorting assets by different properties (serial, project, location, etc.)
 * - Automatic background updates and caching
 * - Suspense-based loading states
 *
 * @param options - Configuration options for filtering and sortin
 * @returns Object containing the sorted assets and query information
 * @property {Asset[]} assets - The sorted list of assets
 * @property {boolean} isLoading - Whether a background refresh is in progress
 * @property {boolean} isError - Whether the last fetch attempt resulted in an error
 * @property {function} refetch - Function to manually trigger a refresh
 * @throws Will throw a promise during initial load (for Suspense)
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export function useAssets(options: UseAssetsOptions = {}) {
  const { data, ...query } = useSuspenseQuery({
    queryKey: ['asset', serializeAssetFilter(options.filter)],
    queryFn: () => getAssets(options.filter),
    refetchOnWindowFocus: true,
    staleTime: ONE_MINUTE_MS,
    refetchInterval: TWO_MINUTES_MS,
  });

  return { assets: data.assets, totalCount: data.totalCount, ...query };
}
